<template>
  <div
    v-if="pageLoading"
    class="m-3"
  >
    <placeholder-content :is-load="true" />
  </div>
  <div
    v-else
  >
    <div class="d-flex justify-content-between border-bottom mb-3">
      <ul class="nav nav-custom">
        <li
          v-for="tab in tabs"
          :key="tab.value"
          class="nav-item"
        >
          <a
            class="nav-link"
            :class="{ active: filterState === tab.value }"
            href="#"
            @click.prevent="setFilterState(tab.value)"
          >
            {{ $t(tab.label) }}
            <span
              class="badge rounded-pill"
              :class="filterState === tab.value ? 'bg-primary' : 'bg-secondary'"
            >
              {{ countByState(tab.value) }}
            </span>
          </a>
        </li>
      </ul>
      <column-toggle
        :headers="allHeaders"
        storage-key="reservations.columns"
        v-model="visibleColumns"
      />
    </div>
    <div class="d-flex justify-content-start mx-3 flex-column flex-sm-row">
      <div class="btn-group btn-group-sm me-sm-2 mb-3">
        <button
          type="button"
          class="btn btn-sm btn-primary"
          @click="openCreate"
        >
          <i class="fa-solid fa-circle-plus me-2" />
          {{ $t('Create') }}
        </button>
      </div>

      <div class="custom-input-group input-group input-group-sm me-sm-2 mb-3 w-auto">
        <input
          type="search"
          class="form-control"
          :placeholder="$t('search')+'...'"
          aria-label="Search"
          v-model.trim="params.search"
        >
        <button
          class="input-group-text"
          type="button"
        >
          <i class="fas fa-search" />
        </button>
      </div>
      <per-page
        @per-page="setPerPage"
      />
    </div>

    <vue3-easy-data-table
      v-model:server-options="datatable.serverOptions"
      ref="dataTable"
      :headers="visibleHeaders"
      :items="pagedItems"
      :loading="datatable.loading"
      :server-items-length="filteredItems.length"
      :theme-color="'#515151'"
      :hide-footer="false"
      :hide-rows-per-page="true"
      :empty-message="$t('No Available Data')"
      :table-min-height="null"
      buttons-pagination
      :body-item-class-name="bodyItemClassNameFunction"
      table-class-name='customize-table'
    >
      <template #header-guests="header">
        <span class="w-100 text-end">
          {{ header.text }}
        </span>
      </template>
      <template #item-customer_name="item">
        <div class="fw-semibold">{{ item.customer_name }}</div>
      </template>
      <template #item-branch="item">
        {{ item.branch ? $t(item.branch) : '-' }}
      </template>
      <template #item-table="item">
        <span v-if="item.table">
          {{ $t(item.table) }}
          <small class="text-muted d-block">{{ $t(item.zone) }}</small>
        </span>
        <span v-else>-</span>
      </template>
      <template #item-guests="item">
        <i class="fa-solid fa-user-group me-1 text-muted" />
        {{ formatNumber(item.guests) }}
      </template>
      <template #item-reservation_at="item">
        <div>{{ formatDate(item.reservation_at) }}</div>
        <small class="text-muted">{{ formatTime(item.reservation_at) }}</small>
      </template>
      <template #item-state="item">
        <span
          class='badge'
          :class="badgeClassByState(item.state)"
        >
          {{ $t(item.state) }}
        </span>
      </template>
      <template #item-note="item">
        <span class="text-muted small">{{ item.note || '-' }}</span>
      </template>
      <template #item-action="item">
        <div class="icon-link custom-actions">
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="openEdit(item)"
            >
              <i class="fa-solid fa-square-pen" />
            </button>
            <template #content>
              <div>{{ $t('Edit') }}</div>
            </template>
          </popper>
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="handleDelete(item.id)"
            >
              <i class="fa-solid fa-trash-can" />
            </button>
            <template #content>
              <div>{{ $t('Delete') }}</div>
            </template>
          </popper>
        </div>
      </template>
      <template #loading>
        <i class="fa-solid fa-spinner fa-spin fa-2xl" />
      </template>
    </vue3-easy-data-table>

    <offcanvas-form
      v-if="offcanvasFroms.open"
      :type="offcanvasFroms.type"
      :id="offcanvasFroms.id"
      :branches="mockBranches"
      :tables="mockTables"
      :state-options="stateOptions"
      :initial-data="offcanvasFroms.initialData"
    />
  </div>
</template>

<script lang="js">
import Vue3EasyDataTable from 'vue3-easy-data-table';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import PerPage from '@/plugins/PerPage.vue';
import FieldsDef from "./assets/template/datatable/FieldsDef";
import OffcanvasForm from './OffcanvasForm.vue';
import ColumnToggle from '@/components/shared/ColumnToggle.vue';
import { loadVisibleColumns } from '@/utils/columnToggle';
import moment from "moment";
import numeral from 'numeral';
import Popper from "vue3-popper";

const mockBranches = [
  { id: 1, name: 'Sukhumvit Branch' },
  { id: 2, name: 'Silom Branch' },
  { id: 3, name: 'Chiang Mai Branch' }
];

const mockTables = [
  { id: 11, name: 'A1', zone: 'Indoor',  branch_id: 1 },
  { id: 12, name: 'A2', zone: 'Indoor',  branch_id: 1 },
  { id: 13, name: 'B1', zone: 'Outdoor', branch_id: 1 },
  { id: 21, name: 'C1', zone: 'VIP',     branch_id: 2 },
  { id: 22, name: 'C2', zone: 'VIP',     branch_id: 2 },
  { id: 23, name: 'D1', zone: 'Indoor',  branch_id: 2 },
  { id: 31, name: 'E1', zone: 'Garden',  branch_id: 3 },
  { id: 32, name: 'E2', zone: 'Garden',  branch_id: 3 }
];

const stateOptions = [
  { value: 'Pending',   text: 'Pending' },
  { value: 'Confirmed', text: 'Confirmed' },
  { value: 'Seated',    text: 'Seated' },
  { value: 'Completed', text: 'Completed' },
  { value: 'Cancelled', text: 'Cancelled' },
  { value: 'No-Show',   text: 'No-Show' }
];

const buildMockReservations = () => ([
  {
    id: 1, code: 'RSV-2026-0001', customer_name: 'Somchai Jaidee', telephone: '081-234-5678',
    branch_id: 1, branch: 'Sukhumvit Branch', table_id: 11, table: 'A1', zone: 'Indoor',
    guests: 4, reservation_at: '2026-05-16 18:30:00', state: 'Confirmed', note: 'Window seat preferred'
  },
  {
    id: 2, code: 'RSV-2026-0002', customer_name: 'Anna Carter', telephone: '089-111-2222',
    branch_id: 2, branch: 'Silom Branch', table_id: 21, table: 'C1', zone: 'VIP',
    guests: 2, reservation_at: '2026-05-16 19:00:00', state: 'Pending', note: 'Anniversary dinner'
  },
  {
    id: 3, code: 'RSV-2026-0003', customer_name: 'Niran Boonmee', telephone: '062-555-0001',
    branch_id: 1, branch: 'Sukhumvit Branch', table_id: 13, table: 'B1', zone: 'Outdoor',
    guests: 6, reservation_at: '2026-05-17 12:00:00', state: 'Seated', note: ''
  },
  {
    id: 4, code: 'RSV-2026-0004', customer_name: 'Yuki Tanaka', telephone: '098-765-4321',
    branch_id: 3, branch: 'Chiang Mai Branch', table_id: 31, table: 'E1', zone: 'Garden',
    guests: 3, reservation_at: '2026-05-15 19:30:00', state: 'Completed', note: 'Vegetarian menu'
  },
  {
    id: 5, code: 'RSV-2026-0005', customer_name: 'Pim Suntornkul', telephone: '085-222-3333',
    branch_id: 2, branch: 'Silom Branch', table_id: 23, table: 'D1', zone: 'Indoor',
    guests: 5, reservation_at: '2026-05-14 20:00:00', state: 'Cancelled', note: 'Customer cancelled'
  },
  {
    id: 6, code: 'RSV-2026-0006', customer_name: 'Mark Williams', telephone: '063-987-6543',
    branch_id: 1, branch: 'Sukhumvit Branch', table_id: 12, table: 'A2', zone: 'Indoor',
    guests: 2, reservation_at: '2026-05-13 18:00:00', state: 'No-Show', note: ''
  },
  {
    id: 7, code: 'RSV-2026-0007', customer_name: 'Kanya Phetkasem', telephone: '081-999-0011',
    branch_id: 3, branch: 'Chiang Mai Branch', table_id: 32, table: 'E2', zone: 'Garden',
    guests: 4, reservation_at: '2026-05-18 17:30:00', state: 'Confirmed', note: 'Birthday cake at 19:00'
  },
  {
    id: 8, code: 'RSV-2026-0008', customer_name: 'Daniel Lee', telephone: '084-321-7777',
    branch_id: 2, branch: 'Silom Branch', table_id: 22, table: 'C2', zone: 'VIP',
    guests: 8, reservation_at: '2026-05-19 19:00:00', state: 'Pending', note: 'Company dinner'
  }
]);

export default {
  data() {
    return {
      params: {
        limit: 10,
        page: 1,
        search: ''
      },
      pageLoading: false,
      filterState: 'ALL',
      tabs: [
        { value: 'ALL',       label: 'All' },
        { value: 'Pending',   label: 'Pending' },
        { value: 'Confirmed', label: 'Confirmed' },
        { value: 'Seated',    label: 'Seated' },
        { value: 'Completed', label: 'Completed' },
        { value: 'Cancelled', label: 'Cancelled' },
        { value: 'No-Show',   label: 'No-Show' }
      ],
      mockBranches,
      mockTables,
      stateOptions,
      datatable: {
        loading: false,
        headers: FieldsDef,
        items: [],
        serverOptions: {
          page: 1,
          rowsPerPage: 10
        }
      },
      offcanvasFroms: {
        open: false,
        type: 'CREATE',
        id: null,
        initialData: null
      },
      nextId: 9,
      visibleColumns: loadVisibleColumns('reservations.columns', FieldsDef(t => t))
    }
  },
  components: {
    PlaceholderContent,
    PerPage,
    Vue3EasyDataTable,
    OffcanvasForm,
    ColumnToggle,
    Popper
  },
  mounted() {
    this.pageLoading = true;
    this.datatable.items = buildMockReservations();
    this.pageLoading = false;
  },
  computed: {
    allHeaders() {
      return this.datatable.headers(this.$t);
    },
    visibleHeaders() {
      return this.allHeaders.filter(h => !h.togglable || this.visibleColumns.includes(h.value));
    },
    filteredItems() {
      let items = this.datatable.items;

      if (this.filterState !== 'ALL') {
        items = items.filter(i => i.state === this.filterState);
      }

      const term = (this.params.search || '').toLowerCase();
      if (term) {
        items = items.filter(i =>
          (i.code && i.code.toLowerCase().includes(term)) ||
          (i.customer_name && i.customer_name.toLowerCase().includes(term)) ||
          (i.telephone && i.telephone.toLowerCase().includes(term)) ||
          (i.branch && i.branch.toLowerCase().includes(term)) ||
          (i.table && i.table.toLowerCase().includes(term))
        );
      }
      return items;
    },
    pagedItems() {
      const page = this.datatable.serverOptions.page;
      const limit = this.datatable.serverOptions.rowsPerPage;
      const start = (page - 1) * limit;
      return this.filteredItems.slice(start, start + limit);
    }
  },
  methods: {
    bodyItemClassNameFunction(column, rowNumber) {
      let className = 'align-top py-1 px-2';
      const columnEnd = ['guests'];
      if (columnEnd.includes(column)) {
        className += ' text-end';
      }
      return className;
    },
    setFilterState(value) {
      this.filterState = value;
      this.datatable.serverOptions.page = 1;
    },
    countByState(value) {
      if (value === 'ALL') return this.datatable.items.length;
      return this.datatable.items.filter(i => i.state === value).length;
    },
    badgeClassByState(state) {
      switch (state) {
        case 'Pending':   return 'bg-warning text-dark';
        case 'Confirmed': return 'bg-info text-dark';
        case 'Seated':    return 'bg-success';
        case 'Completed': return 'bg-primary';
        case 'Cancelled': return 'bg-danger';
        case 'No-Show':   return 'bg-secondary';
        default:          return 'bg-secondary';
      }
    },
    setPerPage(perPage) {
      this.datatable.serverOptions.rowsPerPage = perPage;
      this.$refs.dataTable.updateRowsPerPageActiveOption(perPage);
    },
    formatDate(value) {
      return value ? moment(value).format('YYYY-MM-DD') : '-';
    },
    formatTime(value) {
      return value ? moment(value).format('HH:mm') : '';
    },
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    nextCode() {
      const next = String(this.nextId).padStart(4, '0');
      return `RSV-2026-${next}`;
    },
    enrichForm(form) {
      const branch = this.mockBranches.find(b => b.id === form.branch_id);
      const table = this.mockTables.find(t => t.id === form.table_id);
      return {
        ...form,
        branch: branch ? branch.name : '',
        table: table ? table.name : '',
        zone: table ? table.zone : '',
        reservation_at: form.reservation_at ? moment(form.reservation_at).format('YYYY-MM-DD HH:mm:ss') : ''
      };
    },
    openCreate() {
      this.offcanvasFroms.id = null;
      this.offcanvasFroms.initialData = null;
      this.offcanvasFroms.type = 'CREATE';
      this.offcanvasFroms.open = true;
    },
    openEdit(item) {
      this.offcanvasFroms.id = item.id;
      this.offcanvasFroms.type = 'EDIT';
      this.offcanvasFroms.initialData = {
        customer_name: item.customer_name,
        telephone: item.telephone,
        branch_id: item.branch_id,
        table_id: item.table_id,
        guests: item.guests,
        reservation_at_raw: item.reservation_at ? moment(item.reservation_at).format('YYYY-MM-DDTHH:mm') : '',
        state: item.state,
        note: item.note
      };
      this.offcanvasFroms.open = true;
    },
    handleSave(id, form, type) {
      const enriched = this.enrichForm(form);
      if (type === 'CREATE') {
        const newItem = {
          id: this.nextId,
          code: this.nextCode(),
          ...enriched
        };
        this.nextId += 1;
        this.datatable.items.unshift(newItem);
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Save Reservation') + '!');
      } else {
        const idx = this.datatable.items.findIndex(i => i.id === id);
        if (idx !== -1) {
          this.datatable.items[idx] = {
            ...this.datatable.items[idx],
            ...enriched
          };
        }
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Reservation') + '!');
      }
    },
    handleDelete(id) {
      const self = this;
      const title = this.$t("Delete this Reservation") + '?';
      const html = this.$t("You will not be able to undo this action.");
      const confirmButtonText =
        '<i class="fas fa-check-circle me-1"></i>' + this.$t("Confirm");
      this.$swal({
        icon: "warning",
        iconColor: "#FF7043",
        title,
        html,
        showCancelButton: true,
        cancelButtonText: this.$t("Cancel"),
        confirmButtonText,
        buttonsStyling: false,
        customClass: {
          confirmButton: "btn btn-primary me-2",
          cancelButton: "btn btn-danger",
        },
        focusConfirm: false,
        preConfirm() {
          self.datatable.items = self.datatable.items.filter(i => i.id !== id);
          self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Delete Success!'));
        }
      });
    }
  },
  watch: {
    "datatable.serverOptions" (nval) {
      this.params.page = nval.page;
      this.params.limit = nval.rowsPerPage;
    },
    "params.search" () {
      this.datatable.serverOptions.page = 1;
    }
  }
}
</script>
